import { ChainId, Pair, Token } from '@uniswap/sdk'
import { useCallback, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import { useActiveWeb3React } from '../../hooks'
import { useV2FactoryContract } from '../../hooks/useContract'
import { usePairsForChain } from '../lists/hooks'
import { useSingleContractMultipleData } from '../multicall/hooks'
import { AppDispatch, AppState } from '../index'
import {
  addSerializedPair,
  addSerializedToken,
  removeSerializedToken,
  SerializedPair,
  SerializedToken,
  updateUserDeadline,
  updateUserExpertMode,
  updateUserSlippageTolerance
} from './actions'

function serializeToken(token: Token): SerializedToken {
  return {
    chainId: token.chainId,
    address: token.address,
    decimals: token.decimals,
    symbol: token.symbol,
    name: token.name
  }
}

function deserializeToken(serializedToken: SerializedToken): Token {
  return new Token(
    serializedToken.chainId,
    serializedToken.address,
    serializedToken.decimals,
    serializedToken.symbol,
    serializedToken.name
  )
}

export function useIsDarkMode(): boolean {
  // Force dark mode
  return true
}

export function useDarkModeManager(): [boolean, () => void] {
  // Dark mode is fixed, cannot be toggled
  return [true, () => {}] // eslint-disable-line @typescript-eslint/no-empty-function
}

export function useIsExpertMode(): boolean {
  return useSelector<AppState, AppState['user']['userExpertMode']>(state => state.user.userExpertMode)
}

export function useExpertModeManager(): [boolean, () => void] {
  const dispatch = useDispatch<AppDispatch>()
  const expertMode = useIsExpertMode()

  const toggleSetExpertMode = useCallback(() => {
    dispatch(updateUserExpertMode({ userExpertMode: !expertMode }))
  }, [expertMode, dispatch])

  return [expertMode, toggleSetExpertMode]
}

export function useUserSlippageTolerance(): [number, (slippage: number) => void] {
  const dispatch = useDispatch<AppDispatch>()
  const userSlippageTolerance = useSelector<AppState, AppState['user']['userSlippageTolerance']>(state => {
    return state.user.userSlippageTolerance
  })

  const setUserSlippageTolerance = useCallback(
    (userSlippageTolerance: number) => {
      dispatch(updateUserSlippageTolerance({ userSlippageTolerance }))
    },
    [dispatch]
  )

  return [userSlippageTolerance, setUserSlippageTolerance]
}

export function useUserDeadline(): [number, (slippage: number) => void] {
  const dispatch = useDispatch<AppDispatch>()
  const userDeadline = useSelector<AppState, AppState['user']['userDeadline']>(state => {
    return state.user.userDeadline
  })

  const setUserDeadline = useCallback(
    (userDeadline: number) => {
      dispatch(updateUserDeadline({ userDeadline }))
    },
    [dispatch]
  )

  return [userDeadline, setUserDeadline]
}

export function useAddUserToken(): (token: Token) => void {
  const dispatch = useDispatch<AppDispatch>()
  return useCallback(
    (token: Token) => {
      dispatch(addSerializedToken({ serializedToken: serializeToken(token) }))
    },
    [dispatch]
  )
}

export function useRemoveUserAddedToken(): (chainId: number, address: string) => void {
  const dispatch = useDispatch<AppDispatch>()
  return useCallback(
    (chainId: number, address: string) => {
      dispatch(removeSerializedToken({ chainId, address }))
    },
    [dispatch]
  )
}

export function useUserAddedTokens(): Token[] {
  const { chainId } = useActiveWeb3React()
  const serializedTokensMap = useSelector<AppState, AppState['user']['tokens']>(({ user: { tokens } }) => tokens)

  return useMemo(() => {
    if (!chainId) return []
    return Object.values(serializedTokensMap[chainId as ChainId] ?? {}).map(deserializeToken)
  }, [serializedTokensMap, chainId])
}

function serializePair(pair: Pair): SerializedPair {
  return {
    token0: serializeToken(pair.token0),
    token1: serializeToken(pair.token1)
  }
}

export function usePairAdder(): (pair: Pair) => void {
  const dispatch = useDispatch<AppDispatch>()

  return useCallback(
    (pair: Pair) => {
      dispatch(addSerializedPair({ serializedPair: serializePair(pair) }))
    },
    [dispatch]
  )
}

/**
 * Given two tokens return the liquidity token that represents its liquidity shares
 * @param tokenA one of the two tokens
 * @param tokenB the other token
 * @deprecated Use useV2LiquidityTokens hook instead for correct pair addresses
 */
export function toV2LiquidityToken([tokenA, tokenB]: [Token, Token]): Token {
  return new Token(tokenA.chainId, Pair.getAddress(tokenA, tokenB), 18, 'MINAX-LP', 'Minax DEX LP')
}

/**
 * Hook to get V2 liquidity tokens with correct pair addresses from factory
 * @param tokenPairs Array of token pairs
 * @returns Array of liquidity tokens with correct addresses, or undefined if still loading
 */
export function useV2LiquidityTokens(
  tokenPairs: [Token, Token][]
): { liquidityToken: Token; tokens: [Token, Token] }[] {
  const factoryContract = useV2FactoryContract()

  // Create call inputs for factory.getPair(tokenA, tokenB)
  const pairCallInputs = useMemo(
    () =>
      tokenPairs.map(([tokenA, tokenB]) => {
        const [token0, token1] = tokenA.sortsBefore(tokenB) ? [tokenA, tokenB] : [tokenB, tokenA]
        return [token0.address, token1.address]
      }),
    [tokenPairs]
  )

  // Fetch pair addresses from factory contract
  const pairAddressResults = useSingleContractMultipleData(factoryContract, 'getPair', pairCallInputs)

  return useMemo(() => {
    return tokenPairs.map((tokens, i) => {
      const [tokenA, tokenB] = tokens
      const result = pairAddressResults[i]
      let pairAddress: string

      if (result && !result.loading && result.result) {
        pairAddress = result.result[0]
        // If pair doesn't exist, use zero address (balance will be 0)
        if (pairAddress === '0x0000000000000000000000000000000000000000') {
          pairAddress = '0x0000000000000000000000000000000000000001' // Dummy address for non-existent pairs
        }
      } else {
        // Still loading or error - use fallback computed address
        pairAddress = Pair.getAddress(tokenA, tokenB)
      }

      return {
        liquidityToken: new Token(tokenA.chainId, pairAddress, 18, 'MINAX-LP', 'Minax DEX LP'),
        tokens
      }
    })
  }, [tokenPairs, pairAddressResults])
}

/**
 * Returns all the pairs of tokens that are tracked by the user for the current chain ID.
 * Uses the remote pair list configuration from GitHub.
 */
export function useTrackedTokenPairs(): [Token, Token][] {
  const { chainId } = useActiveWeb3React()

  // Get pairs from remote configuration
  const remotePairs = usePairsForChain(chainId)

  // Convert remote pair info to Token pairs
  const configuredPairs: [Token, Token][] = useMemo(() => {
    return remotePairs.map(pairInfo => {
      const token0 = new Token(
        pairInfo.chainId,
        pairInfo.token0.address,
        pairInfo.token0.decimals,
        pairInfo.token0.symbol,
        pairInfo.token0.name
      )
      const token1 = new Token(
        pairInfo.chainId,
        pairInfo.token1.address,
        pairInfo.token1.decimals,
        pairInfo.token1.symbol,
        pairInfo.token1.name
      )
      return [token0, token1] as [Token, Token]
    })
  }, [remotePairs])

  // pairs saved by users (imported via Pool Finder)
  const savedSerializedPairs = useSelector<AppState, AppState['user']['pairs']>(({ user: { pairs } }) => pairs)

  const userPairs: [Token, Token][] = useMemo(() => {
    if (!chainId || !savedSerializedPairs) return []
    const forChain = savedSerializedPairs[chainId]
    if (!forChain) return []

    return Object.keys(forChain).map(pairId => {
      return [deserializeToken(forChain[pairId].token0), deserializeToken(forChain[pairId].token1)]
    })
  }, [savedSerializedPairs, chainId])

  // Combine configured pairs with user-imported pairs
  const combinedList = useMemo(() => userPairs.concat(configuredPairs), [configuredPairs, userPairs])

  return useMemo(() => {
    // dedupes pairs of tokens in the combined list
    const keyed = combinedList.reduce<{ [key: string]: [Token, Token] }>((memo, [tokenA, tokenB]) => {
      const sorted = tokenA.sortsBefore(tokenB)
      const key = sorted ? `${tokenA.address}:${tokenB.address}` : `${tokenB.address}:${tokenA.address}`
      if (memo[key]) return memo
      memo[key] = sorted ? [tokenA, tokenB] : [tokenB, tokenA]
      return memo
    }, {})

    return Object.keys(keyed).map(key => keyed[key])
  }, [combinedList])
}
